import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { Face } from '../models/face.interface';
import { Offense, Person } from '../models/person.interface';
import { photo } from 'src/utils/faces';
import { ODataArgs, odataParams, ODataResult } from 'src/app/utils/odata';
import { Exception } from '../models/exception';
import { ExceptionAdd } from '../models/exception-add';
import { RenaperResponse } from '../models/renaper';
import { List } from '../../lists/models/list.interface';
import { ExceptionReason } from '../../../models/exception-reason';
import { ConfigService } from 'src/app/services/config.service';

@Injectable({ providedIn: 'root' })
export class PeopleService {
  endpoint: string;

  constructor(private http: HttpClient, private configService: ConfigService) {
    this.endpoint = this.configService.apiUrl + '/people';
  }

  list(args?: Partial<ODataArgs>) {
    let params = new HttpParams();
    params = odataParams(params, args);

    return this.http.get<ODataResult<Person>>(`${this.endpoint}`, { params });
  }

  public getPersonLists(
    person: Person,
    args?: Partial<ODataArgs>
  ): Observable<ODataResult<List>> {
    let params = new HttpParams();
    params = odataParams(params, args);
    const url = `${this.endpoint}/${person.personId}/peopleLists`;
    return this.http.get<ODataResult<List>>(url, { params });
  }

  excludePeopleFromList(
    id: number,
    args?: Partial<ODataArgs>
  ): Observable<ODataResult<Person>> {
    let params = new HttpParams();
    params = params.set('peopleLists', id.toString());
    params = odataParams(params, args);
    const url = `${this.configService.apiUrl}/ExcludedPeopleFromLists`;
    return this.http.get<ODataResult<Person>>(url, { params });
  }

  create(person) {
    return this.http.post(`${this.endpoint}`, person);
  }

  get(id: number) {
    return this.http.get<Person>(`${this.endpoint}/${id}`);
  }

  addFace(id: number, data: FormData) {
    return this.http.post<Face>(`${this.endpoint}/${id}/faces`, data);
  }

  deleteFace(id: number): Observable<any> {
    const url = `${this.configService.apiUrl}/faces/${id}`;
    return this.http.delete(url);
  }

  faces(id: number, args: Partial<ODataArgs>) {
    let params = new HttpParams();
    params = odataParams(params, args);
    return this.http.get<ODataResult<Face>>(`${this.endpoint}/${id}/faces`, {
      params,
    });
  }

  face(personId: number) {
    return this.faces(personId, { top: 1 }).pipe(map(({ items }) => items[0]));
  }

  public photoBlob(faceId: number): Observable<Blob> {
    return this.http.get(photo(faceId, this.configService), {
      responseType: 'blob',
    });
  }

  photo(faceId: number) {
    return this.photoBlob(faceId).pipe(
      mergeMap(
        resource =>
          new Promise<string>((resolve, _) => {
            const reader = new FileReader();
            reader.addEventListener(
              'load',
              () => resolve(reader.result as string),
              false
            );
            reader.readAsDataURL(resource);
          })
      )
    );
  }

  public delete(person: Person): Observable<any> {
    const url = `${this.endpoint}/${person.personId}`;
    return this.http.delete(url);
  }

  public getExceptions(
    person: Person,
    args: Partial<ODataArgs> = {}
  ): Observable<ODataResult<Exception>> {
    const url = `${this.endpoint}/${person.personId}/temporaryExceptions`;

    args.orderby = 'endDateTime desc';
    let params = new HttpParams();
    params = odataParams(params, args);
    return this.http.get<ODataResult<Exception>>(url, { params });
  }

  public addException(exception: ExceptionAdd): Observable<Exception> {
    let url = `${this.configService.apiUrl}/temporaryExceptions`;
    if (exception.PeopleListsIds) {
      url += 'Lists';
    }
    return this.http.post<Exception>(url, exception);
  }

  public getExceptionReasons(): Observable<ExceptionReason[]> {
    const url = `${this.configService.apiUrl}/ExceptionReasons`;
    return this.http.get<ExceptionReason[]>(url);
  }

  public getPersonFilters(searchTerm: string): Partial<ODataArgs> {
    let args: Partial<ODataArgs>;
    if (isNaN(searchTerm as any)) {
      searchTerm = searchTerm.toLowerCase(); // Case insensitive search
      let filter = `contains(tolower(names), '${searchTerm}') OR contains(tolower(surname), '${searchTerm}')`;
      filter += ` OR contains('${searchTerm}', tolower(names)) OR contains('${searchTerm}', tolower(surname))`;
      args = { filter };
    } else if (searchTerm !== '') {
      const byData = { dni: `${searchTerm}%` };
      args = { byData };
    }
    return args;
  }

  public renaper(face: Face): Observable<RenaperResponse> {
    const url = `${this.configService.apiUrl}/renaper/${face.faceId}`;
    return this.http.post<RenaperResponse>(url, {});
  }

  public deleteException(exceptionId: number): Observable<any> {
    const url = `${this.configService.apiUrl}/temporaryExceptions/${exceptionId}`;
    return this.http.delete(url);
  }

  public removePersonFromList(person: Person, list: List): Observable<any> {
    const url = `${this.configService.apiUrl}/peopleLists/${list.peopleListId}/people/${person.personId}`;
    return this.http.delete(url);
  }

  public getOffenses(
    personId: number,
    args: Partial<ODataArgs>
  ): Observable<ODataResult<Offense>> {
    let params = new HttpParams();
    params = odataParams(params, args);

    const url = `${this.configService.apiUrlV2}/typedPeople/justice/${personId}/offenses`;
    return this.http.get<ODataResult<Offense>>(url, { params });
  }

  public addOffense(personId: number, offense: Offense): Observable<Person> {
    const url = `${this.configService.apiUrlV2}/typedPeople/justice/${personId}/offenses`;
    return this.http.post<Person>(url, offense);
  }

  public deleteOffense(personId: number, offenseUUID: string): Observable<any> {
    const url = `${this.configService.apiUrlV2}/typedPeople/justice/${personId}/offenses/${offenseUUID}`;
    return this.http.delete(url);
  }
}
